import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  Grid,
  Box,
  Card,
  CardHeader,
  Divider,
  IconButton,
  Tab,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useEffect, useRef, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { UnsavedPageWarningProvider } from "../../../../../contexts/UnsavedPageWarningContext";
import Transcript from "./components/Transcript";
import Note from "./components/Note";
import { useCopilot } from "../../../../../hooks/useCopilot";
import useEntitlements from "../../../../../hooks/useEntitlements";
import { useAiScribeUsage } from "../../../../../hooks/useAiScribeUsage";
import { useOrganization } from "../../../../facility/hooks/useOrganization";

interface Props {
  isRecording: boolean;
  readOnly: boolean;
  onCollapse?: () => void;
}

export const ScribeBody = ({ isRecording, readOnly = false }: Props) => {
  const { copilotActive, copilotData } = useCopilot();
  const [tabValue, setTabValue] = useState<"1" | "2">(() =>
    copilotData?.id && copilotData?.note && !copilotActive ? "2" : "1",
  );
  const [isGeneratingNote, setIsGeneratingNote] = useState(false);

  return (
    <Box
      sx={{
        position: "relative",
        width: "100%",
        minHeight: isGeneratingNote ? 280 : undefined,
      }}
    >
      {isGeneratingNote && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundColor: "rgba(255, 255, 255, 0.85)",
            backdropFilter: "blur(4px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
            borderRadius: 1,
            gap: 2,
          }}
        >
          <CircularProgress size={60} thickness={4} />
          <Typography
            variant="h6"
            color="primary"
            fontWeight="bold"
            textAlign="center"
          >
            {!copilotData?.note ? "Generating" : "Regenerating"} Note...
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Please wait while the AI processes your transcript
          </Typography>
        </Box>
      )}

      <Grid container>
        <TabContext value={tabValue}>
          <Box borderBottom={1} sx={{ borderColor: "divider", width: "100%" }}>
            <TabList
              aria-label="copilot-tabs"
              variant="fullWidth"
              onChange={(e, newValue) => setTabValue(newValue)}
            >
              <Tab label="Transcript" value="1" />
              <Tab label="AI Note" value="2" />
            </TabList>
          </Box>
          <Box
            id="copilot-scroll-container"
            sx={{ overflowY: "auto", maxHeight: "calc(100vh - 220px)" }}
          >
            <TabPanel value="1">
              <Transcript
                isRecording={isRecording}
                onNavigateToNote={() => setTabValue("2")}
              />
            </TabPanel>
            <TabPanel value="2">
              <Note
                isRecording={isRecording}
                readOnly={readOnly}
                isGeneratingNote={isGeneratingNote}
                onGeneratingChange={setIsGeneratingNote}
              />
            </TabPanel>
          </Box>
        </TabContext>
      </Grid>
    </Box>
  );
};

const Copilot = ({ isRecording, readOnly = false, onCollapse }: Props) => {
  const { isProcessingTranscript } = useCopilot();
  const { organizationReference, organization } = useOrganization();
  const { scribeLowMinuteThreshold } = useFlags();
  const lowMinuteThreshold: number | null =
    scribeLowMinuteThreshold?.value ?? null;
  const { entitlements } = useEntitlements();
  const { data: scribeUsage } = useAiScribeUsage(
    organizationReference?.identifier,
    organization?.adminSettings,
    entitlements,
  );
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsStuck(!entry.isIntersecting),
      { threshold: 1, rootMargin: "-1px 0px 0px 0px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // Own provider when used as a sibling of encounter forms; no-op when nested
  // under AiNoteDetail (or any outer UnsavedPageWarningProvider) so leave
  // guards share one navigator patch.
  return (
    <UnsavedPageWarningProvider>
      <div ref={sentinelRef} style={{ height: 1 }} />
      <Grid container style={{ paddingTop: isStuck ? 0 : 10 }}>
        <Grid
          item
          xs={12}
          sx={{
            ...(isRecording
              ? {
                  border: 4,
                  animation: "pulse 2s infinite",
                  "@keyframes pulse": {
                    "0%": { borderColor: "transparent" },
                    "50%": { borderColor: "rgba(255, 0, 0, 0.8)" },
                    "100%": { borderColor: "transparent" },
                  },
                }
              : {}),
          }}
        >
          <Box id="soap-medical-encounter-container">
            <Card>
              <CardHeader
                action={
                  onCollapse && (
                    <Tooltip title="Collapse Scribe">
                      <IconButton onClick={onCollapse} size="small">
                        <ChevronRightIcon />
                      </IconButton>
                    </Tooltip>
                  )
                }
                title={
                  <Box display="flex" alignItems="center" gap={2}>
                    <Typography variant="h6">Your AI Scribe</Typography>
                    <Chip
                      label="BETA"
                      size="small"
                      color="primary"
                      sx={{ height: 20 }}
                    />
                    {isRecording &&
                      (() => {
                        const lowMinutes =
                          scribeUsage &&
                          !scribeUsage.isUnlimited &&
                          scribeUsage.remainingMinutes != null &&
                          lowMinuteThreshold != null &&
                          scribeUsage.remainingMinutes < lowMinuteThreshold;

                        return (
                          <Typography
                            variant="body1"
                            color={"error"}
                            sx={lowMinutes ? { fontWeight: 500 } : undefined}
                          >
                            {lowMinutes
                              ? `[${scribeUsage.remainingMinutes} ${scribeUsage.remainingMinutes === 1 ? "minute" : "minutes"} of transcribing left.]`
                              : "[Transcribing...]"}
                          </Typography>
                        );
                      })()}
                    {!isRecording && isProcessingTranscript && (
                      <Box display="flex" alignItems="center" gap={1}>
                        <CircularProgress size={16} />
                        <Typography variant="body1" color="primary">
                          Processing transcript...
                        </Typography>
                      </Box>
                    )}
                  </Box>
                }
              />
              <Divider />
              <ScribeBody isRecording={isRecording} readOnly={readOnly} />
            </Card>
          </Box>
        </Grid>
      </Grid>
    </UnsavedPageWarningProvider>
  );
};

export default Copilot;
